import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import EmptyState from "../components/EmptyState";
import PageHeader from "../components/PageHeader";
import { SetupCoachmark } from "../components/selfSetup/SetupCoachmark";
import { useSelfSetupTour } from "../hooks/useSelfSetupTour";
import { HELP_GUIDE_DOWNLOAD_REPORT } from "../constants/knowledgeBaseTryIt";
import { ZERO_ICONS } from "../constants/zeroIcons";
import { helpI18n } from "../i18n/help";
import { contentBorders } from "../theme/createContentTheme";
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from "../utils/helpGuideSession";
import { formatMonth } from "../utils/numberFormat";
import { useDashboardStore } from "../stores/dashboard.store";
import { useOrganizationStore } from "../stores/organization.store";
import { useKPIStore } from "../stores/kpi.store";
import { buildDashboardReportPdf } from "../utils/dashboardReportPdf.helpers";
import ReportCustomizeDialog from "../components/report/ReportCustomizeDialog";
import {
  getReportSectionsForOrgType,
  type ReportPdfSectionId,
} from "../constants/reportCustomize.catalog";
import {
  loadReportPreferences,
  saveReportPreferences,
} from "../utils/reportPreferences.helpers";

const ALL_TIME_VALUE = "ALL_TIME";

const Report: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { t: tHelp } = useTranslation("translation", { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization, fetchOrganizationById } = useOrganizationStore();
  const {
    statistics,
    aggregatedStatistics,
    isLoading,
    isLoadingAggregated,
    selectedMonth,
    fetchDashboardStatistics,
    fetchAggregatedDashboardStatistics,
    setSelectedMonth,
  } = useDashboardStore();
  const { limits } = useKPIStore();
  const { isStep, tourPhase, setTourPhase, completeStep, dismissTour } =
    useSelfSetupTour();
  const showReportTour = isStep("report");

  const [periodValue, setPeriodValue] = useState<string>("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [helpReportGuide, setHelpReportGuide] = useState(false);
  const [reportGuidePhase, setReportGuidePhase] = useState<"period" | "download">(
    "period",
  );
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [selectedSections, setSelectedSections] = useState<ReportPdfSectionId[]>(
    () => {
      const orgType = useOrganizationStore.getState().selectedOrganization
        ?.organization_type;
      return orgType ? loadReportPreferences(orgType) : [];
    },
  );

  const isAllTime = periodValue === ALL_TIME_VALUE;
  const showPeriodTip =
    (showReportTour && tourPhase === "period") ||
    (helpReportGuide && reportGuidePhase === "period");
  const showDownloadTip =
    (showReportTour && tourPhase === "download") ||
    (helpReportGuide && reportGuidePhase === "download");

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get("helpGuide");
    if (guide !== HELP_GUIDE_DOWNLOAD_REPORT) return;
    markHelpGuideActive(guide);
    setHelpReportGuide(true);
    setReportGuidePhase("period");
    navigate({ pathname: location.pathname, search: "" }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!showPeriodTip && !showDownloadTip) return;
    const id = showPeriodTip ? "help-report-period" : "help-report-download";
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [showPeriodTip, showDownloadTip]);

  const finishReportGuide = () => {
    if (helpReportGuide) {
      setHelpReportGuide(false);
      setReportGuidePhase("period");
      clearHelpGuideActive();
      return;
    }
    if (showReportTour) {
      completeStep("report");
    }
  };

  /** Close tip without completing the report step (Later / X). */
  const softDismissReportGuide = () => {
    if (helpReportGuide) {
      setHelpReportGuide(false);
      setReportGuidePhase("period");
      clearHelpGuideActive();
      return;
    }
    dismissTour();
  };

  const advanceReportTip = () => {
    if (showReportTour && tourPhase === "period") {
      setTourPhase("download");
      return;
    }
    if (helpReportGuide && reportGuidePhase === "period") {
      setReportGuidePhase("download");
      return;
    }
    finishReportGuide();
  };

  const [availableMonths] = useState(() => {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );
      months.push({
        value: format(date, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        label: format(date, "MMMM yyyy"),
      });
    }
    return months;
  });

  useEffect(() => {
    const orgType = selectedOrganization?.organization_type;
    if (!selectedOrganization?.id || !orgType) return;

    const currentDate = new Date();
    const defaultMonth = format(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
      "yyyy-MM-dd'T'HH:mm:ss'Z'"
    );
    const monthToUse = selectedMonth || defaultMonth;
    if (monthToUse !== selectedMonth) {
      setSelectedMonth(monthToUse);
    }
    if (!periodValue) {
      setPeriodValue(monthToUse);
    }

    fetchDashboardStatistics(selectedOrganization.id, orgType, monthToUse);
    setSelectedSections(loadReportPreferences(orgType));
  }, [selectedOrganization?.id, selectedOrganization?.organization_type]);

  useEffect(() => {
    if (
      !isAllTime ||
      !selectedOrganization?.id ||
      !selectedOrganization?.organization_type
    ) {
      return;
    }
    fetchAggregatedDashboardStatistics(
      selectedOrganization.id,
      selectedOrganization.organization_type
    );
  }, [
    isAllTime,
    selectedOrganization?.id,
    selectedOrganization?.organization_type,
    fetchAggregatedDashboardStatistics,
  ]);

  const handlePeriodChange = (value: string) => {
    setPeriodValue(value);

    if (value === ALL_TIME_VALUE) {
      return;
    }

    setSelectedMonth(value);
    if (selectedOrganization?.id && selectedOrganization?.organization_type) {
      fetchDashboardStatistics(
        selectedOrganization.id,
        selectedOrganization.organization_type,
        value
      );
    }
  };

  const displayStatistics = isAllTime ? aggregatedStatistics : statistics;
  const isLoadingStatistics = isAllTime ? isLoadingAggregated : isLoading;

  const handleDownloadReport = async () => {
    if (!displayStatistics || !selectedOrganization) return;
    setIsGeneratingReport(true);

    try {
      const freshOrg = await fetchOrganizationById(selectedOrganization.id);
      const orgForReport = freshOrg ?? selectedOrganization;

      const { doc, filename } = await buildDashboardReportPdf({
        statistics: displayStatistics,
        organizationName: orgForReport.name ?? "Organization",
        organizationLogoUrl: orgForReport.logo_url,
        dashboardViewTab: isAllTime ? 1 : 0,
        selectedMonth,
        limits,
        includeSections: selectedSections,
      });
      doc.save(filename);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  if (!selectedOrganization?.id) {
    return <EmptyState icon={ZERO_ICONS.report} title={t("report.selectOrganization")} minHeight={240} />;
  }

  const monthLabel = (value: string) => {
    const match = /^(\d{4})-(\d{2})/.exec(value);
    return match ? formatMonth(new Date(Number(match[1]), Number(match[2]) - 1, 1), i18n.language) : value;
  };
  const currentPeriod = periodValue || selectedMonth || availableMonths[0]?.value;
  const periodLabel = isAllTime ? t("report.allTimeAggregated") : monthLabel(currentPeriod);
  const orgSections = selectedOrganization.organization_type
    ? getReportSectionsForOrgType(selectedOrganization.organization_type)
    : [];
  const included = orgSections.filter((id) => selectedSections.includes(id));

  return (
    <Box>
      <PageHeader
        icon={<ZERO_ICONS.report />}
        title={t("report.title")}
        subtitle={t("report.subtitle", { name: selectedOrganization.name })}
        actions={
          <>
            <Tooltip title={t("report.customizeSubtitle")}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ZERO_ICONS.customize />}
                onClick={() => setCustomizeOpen(true)}
                aria-label={t("report.customize")}
                sx={{ whiteSpace: "nowrap", gap: 0.5 }}
              >
                {t("report.customize")}
                {included.length < orgSections.length ? (
                  <Chip component="span" label={<bdi>{included.length}</bdi>} size="small" sx={{ marginInlineStart: 0.5 }} />
                ) : null}
              </Button>
            </Tooltip>
            <SetupCoachmark
              open={showPeriodTip}
              title={
                helpReportGuide
                  ? tHelp("knowledgeBase.downloadReportPeriodTitle")
                  : t("selfSetup.tour.reportPeriodTitle")
              }
              subtitle={
                helpReportGuide
                  ? tHelp("knowledgeBase.downloadReportPeriodSubtitle")
                  : t("selfSetup.tour.reportPeriodSubtitle")
              }
              onDismiss={softDismissReportGuide}
              onNext={advanceReportTip}
              nextLabel={t("selfSetup.tourNext")}
            >
              <Select
                id="help-report-period"
                size="small"
                value={currentPeriod}
                onChange={(event) => handlePeriodChange(event.target.value)}
                aria-label={t("report.period")}
                startAdornment={<ZERO_ICONS.period sx={{ fontSize: 18, color: "text.secondary", marginInlineEnd: 1 }} />}
                sx={{ minWidth: 220 }}
                renderValue={(value) => (value === ALL_TIME_VALUE ? t("report.allTime") : monthLabel(String(value)))}
              >
                <MenuItem value={ALL_TIME_VALUE}>{t("report.allTime")}</MenuItem>
                {availableMonths.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {monthLabel(month.value)}
                  </MenuItem>
                ))}
              </Select>
            </SetupCoachmark>
          </>
        }
      />

      {selectedOrganization.organization_type && (
        <ReportCustomizeDialog
          open={customizeOpen}
          orgType={selectedOrganization.organization_type}
          selectedSections={selectedSections}
          onClose={() => setCustomizeOpen(false)}
          onSave={(next) => {
            setSelectedSections(next);
            saveReportPreferences(next);
            setCustomizeOpen(false);
          }}
        />
      )}

      <Box
        component="section"
        sx={(theme) => ({
          maxWidth: 880,
          bgcolor: "background.paper",
          border: `1px solid ${contentBorders(theme).default}`,
          borderRadius: "8px",
          overflow: "hidden",
        })}
      >
        {isLoadingStatistics ? <LinearProgress aria-label={t("common.loading")} /> : null}
        <Box sx={{ p: { xs: 2.5, md: 3.5 }, display: "grid", gap: 3 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box
              aria-hidden
              sx={(theme) => ({
                width: 48,
                height: 56,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                borderRadius: "6px",
                border: `1px solid ${contentBorders(theme).strong}`,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                color: "primary.main",
              })}
            >
              <ZERO_ICONS.report />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" component="p" color="text.secondary">
                {t("report.pdfLabel")}
              </Typography>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                {t("report.cardTitle")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75, flexWrap: "wrap", color: "text.secondary" }}>
                <ZERO_ICONS.period sx={{ fontSize: 16 }} aria-hidden />
                <Typography variant="body2">{periodLabel}</Typography>
                <Typography variant="body2" aria-hidden>
                  ·
                </Typography>
                <Typography variant="body2">{selectedOrganization.name}</Typography>
              </Box>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "72ch" }}>
            {included.length === 0 ? t("report.noSections") : t("report.cardDescription")}
          </Typography>

          {!isLoadingStatistics && !displayStatistics ? (
            <Alert severity="info">{t("report.noData")}</Alert>
          ) : null}

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <SetupCoachmark
              open={showDownloadTip}
              title={
                helpReportGuide
                  ? tHelp("knowledgeBase.downloadReportButtonTitle")
                  : t("selfSetup.tour.reportDownloadTitle")
              }
              subtitle={
                helpReportGuide
                  ? tHelp("knowledgeBase.downloadReportButtonSubtitle")
                  : t("selfSetup.tour.reportDownloadSubtitle")
              }
              onDismiss={softDismissReportGuide}
              onNext={finishReportGuide}
              nextLabel={t("selfSetup.tourDone")}
            >
              <Button
                id="help-report-download"
                variant="contained"
                size="large"
                startIcon={
                  isGeneratingReport ? <CircularProgress size={18} color="inherit" /> : <ZERO_ICONS.download />
                }
                onClick={() => {
                  void handleDownloadReport();
                  if (helpReportGuide || showReportTour) {
                    finishReportGuide();
                  }
                }}
                disabled={
                  isGeneratingReport ||
                  (!(helpReportGuide || showReportTour) && (isLoadingStatistics || !displayStatistics))
                }
              >
                {isGeneratingReport ? t("report.generating") : t("report.download")}
              </Button>
            </SetupCoachmark>
            <Typography variant="caption" color="text.secondary">
              {t("report.languageNote")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Report;
